var productName = 'scheduler';import Base from '../../../Common/Base.js';
import PresetManager from '../../preset/PresetManager.js';

/**
 * @module Scheduler/view/mixin/TimelineViewPresets
 */

/**
 * View preset handling
 *
 * @mixin
 */
export default Target => class TimelineViewPresets extends (Target || Base) {
    //region Default config

    static get defaultConfig() {
        //if (!PresetManager.presets) PresetManager.registerDefaults();

        return {
            /**
             * A string key used to lookup a predefined {@link Scheduler.preset.ViewPreset} (e.g. 'weekAndDay', 'hourAndDay'),
             * managed by {@link Scheduler.preset.PresetManager}. See {@link Scheduler.preset.PresetManager} for more information.
             * Or a config object for a viewPreset.
             *
             * Options:
             * - 'secondAndMinute'
             * - 'minuteAndHour'
             * - 'hourAndDay'
             * - 'dayAndWeek'
             * - 'weekAndDay'
             * - 'weekAndMonth',
             * - 'monthAndYear'
             * - 'year'
             * - 'manyYears'
             * - 'weekAndDayLetter'
             * - 'weekDateAndMonth'
             * - 'day'
             * - 'week'
             *
             * If passed as a config object, the settings from the viewPreset with the provided 'name' property will be used along
             * with any overridden values in your object.
             *
             * To override:
             * ```javascript
             * viewPreset       : {
             *   name                : 'hourAndDay',
             *   headerConfig        : {
             *       middle          : {
             *           unit       : "hour",
             *           increment  : 12,
             *           renderer   : (startDate, endDate, headerConfig, cellIdx) => {
             *               return "";
             *           }
             *       }
             *   }
             * }
             * ```
             * or set a new valid preset config if the preset is not registered in the {@link Scheduler.preset.PresetManager}.
             *
             * When you use scheduler in weekview mode, this config is used to pick view preset. If passed view preset is not
             * supported by weekview (only 2 supported by default - 'day' and 'week') default preset will be used - 'week'.
             * @config {String|Object}
             * @default
             * @category Common
             */
            viewPreset : 'weekAndDayLetter',

            /**
             * Defines how dates will be formatted in tooltips etc. This config has priority over similar config on the
             * view preset. For allowed values see {@link Common.helper.DateHelper##function-format-static}.
             * @config {String}
             * @category Scheduled events
             */
            displayDateFormat : null
        };
    }

    //endregion

    //region Get/set

    /**
     * Get/set the current view preset
     * @property {Scheduler.preset.ViewPreset|String}
     * @category Common
    */
    get viewPreset() {
        return this._viewPreset;
    }

    set viewPreset(preset) {
        if (this._viewPreset !== preset) {
            this.setViewPreset(preset);
        }
    }

    /**
     * Sets the current view preset. See the {@link Scheduler.preset.PresetManager} class for details.
     *
     * @param {String|Object|Scheduler.preset.ViewPreset} preset The id of the new preset (see {@link Scheduler.preset.PresetManager} for details)
     * @param {Date} [startDate] A new start date for the time axis
     * @param {Date} [endDate] A new end date for the time axis
     * @private
     */
    setViewPreset(preset, startDate, endDate, initial, options = {}) {
        

        const me = this;

        // This is set if the object is inside the Base constructor's configure method.
        initial = initial || me.isConfiguring;

        let centerDate = options.centerDate,
            timeAxis   = me._timeAxis;  // Do not tickle the getter, we are just peeking to see if it's there yet.

        // normalize preset (applies preset customizations or gets a predefined preset)
        preset = PresetManager.normalizePreset(preset);

        if (initial || me.trigger('beforePresetChange', { preset, startDate, endDate }) !== false) {
            me._viewPreset = preset;

            // prefer to use displayDateFormat configured on the panel
            me.displayDateFormat = me.config.displayDateFormat || preset.displayDateFormat;

            const isHorizontal = me.isHorizontal;

            if (timeAxis) {
                // None of this reconfiguring should cause a refresh
                me.suspendRefresh();

                // Timeaxis may already be configured (in case of sharing with the timeline partner), no need to reconfigure it
                if (!(initial && timeAxis.isConfigured)) {
                    const timeAxisCfg = {
                        weekStartDay : me.weekStartDay,
                        startTime    : me.startTime,
                        endTime      : me.endTime
                    };

                    if (initial) {
                        if (timeAxis.count === 0 || startDate) {
                            timeAxisCfg.startDate = startDate || new Date();
                            timeAxisCfg.endDate = endDate;
                        }
                    }
                    else {
                        // if startDate is provided we use it and the provided endDate
                        if (startDate) {
                            timeAxisCfg.startDate = startDate;
                            timeAxisCfg.endDate = endDate;

                            // if both dates are provided we can calculate centerDate for the viewport
                            if (!centerDate && endDate) {
                                // TODO: PORT infitieScroll stuff
                                // if (me.infiniteScroll && view.cachedScrollDate && view.cachedScrollDateIsCentered) {
                                //     centerDate = view.cachedScrollDate;
                                // } else {
                                centerDate = new Date((startDate.getTime() + endDate.getTime()) / 2);
                                //}
                            }

                            // when no start/end dates are provided we use the current timespan
                        }
                        else {
                            timeAxisCfg.startDate = timeAxis.startDate;
                            timeAxisCfg.endDate = endDate || timeAxis.endDate;

                            if (!centerDate) {
                                // TODO: PORT inifiteScroll stuff
                                // if (me.infiniteScroll && view.cachedScrollDate && view.cachedScrollDateIsCentered) {
                                //     centerDate = view.cachedScrollDate;
                                // } else {
                                // TODO: PORT needed?
                                //centerDate = me.getViewportCenterDateCached();
                                centerDate = me.viewportCenterDate;
                                //}
                            }
                        }
                    }

                    timeAxis.isConfigured = false;
                    timeAxis.viewPreset = preset;
                    timeAxis.reconfigure(timeAxisCfg, true);

                    me.timeAxisViewModel.reconfigure({
                        // update preset name in viewmodel to simplify zooming sync for partner panels
                        viewPreset          : me.viewPreset,
                        headerConfig        : preset.headerConfig,
                        columnLinesFor      : preset.columnLinesFor || 'middle',
                        // TODO: PORT rowHeightHorizontal??
                        rowHeightHorizontal : me.readRowHeightFromPreset ? preset.rowHeight : me.rowHeight,
                        tickSize            : isHorizontal ? preset.tickWidth : preset.tickHeight || preset.tickWidth || 60
                    });

                    // Allow refresh to run after the reconfiguring
                    me.resumeRefresh();
                }

                // TODO: PORT vertical later
                // if (isVertical) {
                //     me.setColumnWidth(me.resourceColumnWidth || preset.resourceColumnWidth || 100, true);
                // }

                me.refresh();

                // if view is rendered and scroll is not disabled by "notScroll" option
                if (!options.notScroll && me.rendered) {
                    // and we have centerDate to scroll to
                    if (centerDate) {
                        // remember the central date we scroll to (it gets reset after user scroll)
                        me.cachedCenterDate = centerDate;

                        let x = null;

                        // TODO: PORT vertical later
                        // if (isVertical) {
                        //     y = Math.max(Math.floor(view.getCoordinateFromDate(centerDate, true) - view.getViewContainerHeight() / 2), 0);
                        //     me.viewPresetActiveScroll = { top : y };
                        //     view.scrollVerticallyTo(y);
                        // } else {
                        x = Math.max(Math.floor(me.getCoordinateFromDate(centerDate, true) - me.timeAxisSubGrid.width / 2), 0);
                        me.viewPresetActiveScroll = { left : x };

                        // The horizontal scroll handler must not invalidate the cached center
                        // when this scroll event rolls round on the next frame.
                        me.scrollingToCenter = true;

                        me.scrollHorizontallyTo(x, false);

                        // Release the lock on scrolling invalidating the cached center.
                        me.setTimeout(() => {
                            me.scrollingToCenter = false;
                        }, 100);
                        //}

                        // if we don't have a central date to scroll at we reset scroll (this is bw compatible behavior)
                    }
                    else {
                        // TODO: PORT vertical later
                        // if (isHorizontal) {
                        me.scrollHorizontallyTo(0, false);
                        //} else {
                        //    view.scrollVerticallyTo(0);
                        //}
                    }
                }

                me.trigger('presetChange', { preset, startDate, endDate, centerDate });
            }
        }
    }

    //endregion

    // This does not need a className on Widgets.
    // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
    // to the Widget it's mixed in to should implement thus.
    get widgetClass() {}
};
